<template>
    <div class="search-diagram" ref="main" :class="css.container">
        <renderHeader />
        <renderContent />
    </div>
</template>

<script lang="tsx" setup>
//* 组件库
import type { UiTableOption2 } from "various-ui";
//* 组件属性
import { SearchDiagramPropsOption, SearchDiagramEmits } from "./src";
import { useComposable } from "./src/composable";
//* 样式（module）
import css from "./style/index.module.less";

//* 组件属性注册
const define = defineProps(SearchDiagramPropsOption);
const emits = defineEmits(SearchDiagramEmits);
const slots = defineSlots();

//* 组合函数
const { refs, nodes, methods, variable, animation } = useComposable(define, emits);
const { main } = nodes;
const { clear, switchSort } = methods;
const { aniEnter, aniLeave } = animation;

//* 触发数据更新时, 进行一次初始化
onUpdated(() => methods.init());

//* 组件挂载时, 注册观察者
onMounted(() => {
    variable.observer.value?.disconnect();
    variable.observer.value = new ResizeObserver(() => {
        if (main.value && variable.size.value != main.value.offsetWidth) {
            methods.init();
        }
    });

    if (nodes.main.value) {
        variable.observer.value.observe(nodes.main.value);
    }
});

//* 组件卸载时, 注销观察者
onBeforeUnmount(() => {
    variable.observer.value?.disconnect();
});

//* 组件属性导出
defineExpose({ clear });

//* 渲染函数（表头）
const renderHeader = defineComponent({
    render() {
        //* 渲染函数（表头列）
        const renderHeaderColumn = (column: UiTableOption2) => {
            //* 渲染函数（表头内容）
            const renderRowContext = () => <span>{slots[column.slot + "Header"]?.() || column.name}</span>;

            //* 渲染函数（表头插槽）
            const renderRowSort = () => {
                if (!column.sort) return;
                if (refs.sortValue.value && refs.sortKey.value == column.key) {
                    return <span class={`${css.sort} ${css[`sort-${refs.sortValue.value}`]}`} onClick={() => switchSort(column)}></span>;
                } else {
                    return <span class={css.sort} onClick={() => switchSort(column)}></span>;
                }
            };

            return (
                <div class={`${css.column} ${column.align ? css[`column-${column.align}`] : ""}`} d-name={column.key}>
                    {renderRowContext()}
                    {renderRowSort()}
                </div>
            );
        };

        return <div class={css.header}>{define.option.map((column) => renderHeaderColumn(column))}</div>;
    },
});

//* 渲染函数（内容）
const renderContent = defineComponent({
    render() {
        //* 渲染函数（内容）
        const renderBodys = () => {
            if (define.data.length && define.option.length) return define.data.map((row, index) => renderBody(row, index));
            else {
                //* 暂无数据
                return (
                    <div class={`${css.body} ${css["body-error"]}`}>
                        <div class={css.row}>{ slots['noResult'] ?  slots['noResult']() : define.placeholder}</div>
                    </div>
                );
            }
        };

        //* 渲染函数（行）
        const renderBody = (row: any, index: number) => {
            const data = Object.assign(row, { mode: define.mode });
            return (
                <div class={css.body} onClick={() => emits("click", data)} onMousemove={() => aniEnter(index)} onMouseleave={() => aniLeave(index)}>
                    <div class={css.row}>{define.option.map((column) => renderColumn(row, column))}</div>
                </div>
            );
        };

        //* 渲染函数（列）
        const renderColumn = (row: any, column: UiTableOption2) => {
            return (
                <div class={`${css.column} ${column.align ? css[`column-${column.align}`] : ""}`} d-name={column.key}>
                    {column.slot ? slots[column.slot]?.({ data: row, option: column }) || row[column.key] : row[column.key]}
                </div>
            );
        };

        return <div class={css.bodys}>{renderBodys()}</div>;
    },
});
</script>
